import { useState, useEffect } from "react";
import axios from "axios";
import { Search, ChevronRight, MapPin, Heart } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/layout/Navbar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categories = [
  { value: "all", label: "Alle" },
  { value: "driver", label: "Driver" },
  { value: "wood", label: "Wood" },
  { value: "jern", label: "Jern" },
  { value: "putter", label: "Putter" },
  { value: "wedge", label: "Wedge" },
  { value: "hybrid", label: "Hybrid" },
  { value: "bag", label: "Bag" },
];

const HomePageGrid = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [favorites, setFavorites] = useState([]);

  useEffect(() => {
    fetchListings();
    loadFavorites();
  }, [selectedCategory]);

  const loadFavorites = () => {
    const stored = localStorage.getItem("golfmarked_favorites");
    if (stored) {
      setFavorites(JSON.parse(stored));
    }
  };

  const fetchListings = async () => {
    try {
      setLoading(true);
      const params = { status: "active" };
      if (selectedCategory !== "all") {
        params.category = selectedCategory;
      }
      const response = await axios.get(`${API}/listings`, { params });
      setListings(response.data);
    } catch (error) {
      console.error("Error fetching listings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      const filtered = listings.filter(l => 
        l.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        l.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setListings(filtered);
    } else {
      fetchListings();
    }
  };

  const toggleFavorite = (listingId, e) => {
    e.stopPropagation();
    let newFavorites;
    if (favorites.includes(listingId)) {
      newFavorites = favorites.filter(id => id !== listingId);
    } else {
      newFavorites = [...favorites, listingId];
    }
    setFavorites(newFavorites);
    localStorage.setItem("golfmarked_favorites", JSON.stringify(newFavorites));
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <Navbar />

      {/* Search Bar */}
      <div className="bg-white border-b border-gray-100 px-4 pb-3">
        <div className="max-w-7xl mx-auto">
          <form onSubmit={handleSearch}>
            <div className="relative max-w-xl">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Søk etter utstyr..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-gray-100 border-0 rounded-full"
              />
            </div>
          </form>
        </div>
      </div>

      {/* Main Content */}
      <main className="px-4 py-4 max-w-7xl mx-auto">
        {/* Guide CTA */}
        <div className="mb-6 p-5 lg:p-6 bg-gradient-to-br from-green-700 to-green-800 rounded-2xl lg:flex lg:items-center lg:justify-between">
          <div>
            <p className="text-green-300 text-sm font-medium mb-1">Ny her?</p>
            <h2 className="text-white font-bold text-xl lg:text-2xl mb-1">La oss hjelpe deg</h2>
            <p className="text-green-200/80 text-sm mb-4 lg:mb-0">
              Svar på noen spørsmål, få personlige anbefalinger
            </p>
          </div>
          <button
            onClick={() => navigate("/utstyrsguide")}
            className="inline-flex items-center gap-2 bg-white text-green-800 px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-green-50 transition-colors"
          >
            Start guiden
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Categories */}
        <div className="mb-5">
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat.value}
                onClick={() => setSelectedCategory(cat.value)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === cat.value
                    ? "bg-green-700 text-white"
                    : "bg-white text-gray-700 border border-gray-200"
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Listings Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-700"></div>
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Ingen annonser funnet</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 lg:gap-4">
            {listings.map((listing) => (
              <div 
                key={listing.id}
                className="bg-white rounded-xl overflow-hidden cursor-pointer hover:shadow-md transition-shadow border border-gray-100"
                onClick={() => navigate(`/listing/${listing.id}`)}
              >
                {/* Image */}
                <div className="aspect-square bg-gray-100 relative">
                  {listing.images && listing.images.length > 0 ? (
                    <img
                      src={listing.images[0]}
                      alt={listing.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <span className="text-sm">Ingen bilde</span>
                    </div>
                  )}
                  <button
                    onClick={(e) => toggleFavorite(listing.id, e)}
                    className="absolute top-2 right-2 w-8 h-8 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center"
                  >
                    <Heart 
                      className={`w-4 h-4 ${
                        favorites.includes(listing.id) 
                          ? "fill-red-500 text-red-500" 
                          : "text-gray-400"
                      }`} 
                    />
                  </button>
                  {listing.condition && (
                    <div className="absolute bottom-2 left-2 flex items-center gap-1.5 px-2 py-1 rounded-full"
                      style={{
                        backgroundColor: 
                          listing.condition === 'ny' || listing.condition === 'new' ? '#1E7F4D' :
                          listing.condition === 'utmerket' || listing.condition === 'excellent' ? '#2F8F9D' :
                          '#D4A017'
                      }}
                    >
                      <span className="text-xs font-medium text-white capitalize">
                        {listing.condition === 'ny' || listing.condition === 'new' ? 'Ny' :
                         listing.condition === 'utmerket' || listing.condition === 'excellent' ? 'Utmerket' :
                         'Brukt'}
                      </span>
                    </div>
                  )}
                </div>
                
                {/* Content */}
                <div className="p-3">
                  <h3 className="font-medium text-gray-900 text-sm line-clamp-1">
                    {listing.title}
                  </h3>
                  
                  <p className="text-green-700 font-bold text-lg mt-1">
                    {listing.price.toLocaleString("nb-NO")} kr
                  </p>
                  
                  <div className="flex items-center gap-1 mt-1.5 text-xs text-gray-500">
                    <MapPin className="w-3 h-3" />
                    <span>{listing.location}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-6 mb-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <button
              onClick={() => navigate("/club-fitting-guide")}
              className="text-gray-500 hover:text-green-700"
            >
              Club Fitting Guide
            </button>
            <button
              onClick={() => navigate("/wedge-grind-guide")}
              className="text-gray-500 hover:text-green-700"
            >
              Wedge Grind Guide
            </button>
            <button
              onClick={() => navigate("/privacy")}
              className="text-gray-500 hover:text-green-700"
            >
              Personvern
            </button>
            <button
              onClick={() => navigate("/admin/login")}
              className="text-gray-400 hover:text-gray-600"
            >
              Admin
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePageGrid;
